import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { matchAPI } from "../services/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import UserProfileModal from "../components/UserProfileModal";
import { useAuth } from "../context/AuthContext";
import LoadingAnimation from "../components/LoadingAnimation";
import { cacheService } from "../services/cache";
import {
  getMatchesLocal,
  setMatchesLocal,
  getMatchDetailLocal,
  setMatchDetailLocal,
} from "../utils/storageHelper";
import { useCleanupOnUnload } from "../hooks/useCleanupOnUnload";
import AuthBackground from "../components/AuthBackground";

const debugLog = () => {};

const resolveProfileId = (profile) => {
  if (!profile) return null;
  return (
    profile.userId ||
    profile._id ||
    profile.id ||
    profile.userID ||
    profile?.user?.id ||
    null
  );
};

function Matches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalUserId, setProfileModalUserId] = useState(null);
  const { unreadByMatch, markMatchRead, setActiveMatchId } = useAuth();
  const navigate = useNavigate();
  const loadInFlightRef = useRef(false);
  const lastLoadedAtRef = useRef(0);

  // Clear localStorage when tab is closed
  useCleanupOnUnload();

  useEffect(() => {
    debugLog("[Matches] unreadByMatch updated:", unreadByMatch);
  }, [unreadByMatch]);

  const loadMatches = useCallback(async (force = false) => {
    const now = Date.now();
    if (loadInFlightRef.current) return;

    // 1. Check localStorage FIRST for instant load
    if (!force) {
      const cached = getMatchesLocal();
      if (cached?.data) {
        console.log("[Matches] Loaded from localStorage");
        setMatches(cached.data);
        setLoading(false);
        return; // No API call needed!
      }
    }

    // 2. Cache miss - fetch from API
    if (!force && now - lastLoadedAtRef.current < 2000) {
      const cachedMatches = cacheService.get("matches");
      if (cachedMatches) {
        setMatches(cachedMatches);
        setLoading(false);
      }
      return;
    }

    loadInFlightRef.current = true;
    setLoading(true);
    try {
      // Use getChatList for priority sorting with special message support
      const chatListResponse = await matchAPI.getChatList();
      const chatList = chatListResponse.data.data;

      // Fetch full match details (with profile) for each chat
      const matchesWithProfiles = await Promise.all(
        chatList.map(async (chatItem) => {
          try {
            // Try cache first
            const cacheKey = `match_${chatItem.matchId}`;
            let matchData = cacheService.get(cacheKey);

            if (!matchData) {
              const matchResponse = await matchAPI.getMatchById(
                chatItem.matchId,
              );
              matchData = matchResponse.data.data;
              // Cache match data for 5 minutes
              cacheService.set(cacheKey, matchData, {
                ttl: 5 * 60 * 1000,
                useSessionStorage: true,
              });
            }

            // Combine chat list metadata with match profile data
            // Store lightweight summary (fast to load from localStorage)
            const summary = {
              matchId: chatItem.matchId,
              name: matchData.profile?.name || "User",
              age: matchData.profile?.age || null,
              bio: matchData.profile?.bio || "",
              profilePhotoUrl: matchData.profile?.photos?.[0] || null,
              otherUserId: matchData.otherUserId,
              unreadSpecialCount: chatItem.unreadSpecialCount,
              unreadNormalCount: chatItem.unreadNormalCount,
              hasUnreadSpecial: chatItem.hasUnreadSpecial,
              latestMessage: chatItem.latestMessage,
              lastMessageAt: chatItem.lastMessageAt,
            };

            // Store full profile separately for when user clicks
            setMatchDetailLocal(chatItem.matchId, matchData);

            return summary;
          } catch (error) {
            console.error("Error fetching match details:", error);
            return null;
          }
        }),
      );

      // Filter out any failed fetches
      const filtered = matchesWithProfiles.filter((m) => m !== null);
      setMatches(filtered);

      // Save to localStorage
      setMatchesLocal(filtered);
      console.log("[Matches] Loaded from API, saved to localStorage");

      // Cache matches list for 2 minutes
      cacheService.set("matches", filtered, {
        ttl: 2 * 60 * 1000,
        useSessionStorage: true,
      });
    } catch (error) {
      console.error("Error loading matches:", error);
    } finally {
      setLoading(false);
      lastLoadedAtRef.current = Date.now();
      loadInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadMatches(true);

    // Refresh matches when returning to this page
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        debugLog("[Matches] Page became visible, refreshing matches");
        loadMatches();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadMatches]);

  const handleChatClick = useCallback(
    (matchId) => {
      setActiveMatchId(matchId.toString());
      markMatchRead(matchId.toString());

      // 1. Check if detail is already cached
      const cached = getMatchDetailLocal(matchId);

      // 2. Navigate IMMEDIATELY (no wait!)
      navigate(`/chat/${matchId}`);

      // 3. Fetch in background ONLY if not cached
      if (!cached?.data) {
        const cacheKey = `match_${matchId}`;
        matchAPI
          .getMatchById(matchId)
          .then((res) => {
            setMatchDetailLocal(matchId, res.data.data);
            cacheService.set(cacheKey, res.data.data, {
              ttl: 5 * 60 * 1000,
              useSessionStorage: true,
            });
          })
          .catch((err) => console.error("Error fetching match detail:", err));
      }
    },
    [setActiveMatchId, markMatchRead, navigate],
  );

  const handleProfileClick = useCallback((match, e) => {
    e.stopPropagation();

    const fullMatch = getMatchDetailLocal(match.matchId);
    const resolvedProfile = fullMatch?.data?.profile || match;
    const resolvedUserId =
      fullMatch?.data?.otherUserId ||
      match.otherUserId ||
      resolveProfileId(resolvedProfile);

    if (!resolvedUserId) {
      console.warn("[Matches] Missing userId for profile view", match);
      return;
    }

    const normalizedId = resolvedUserId?.toString?.() || resolvedUserId;

    setSelectedProfile(resolvedProfile);
    setProfileModalUserId(normalizedId);
    setProfileModalOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setProfileModalOpen(false);
    setSelectedProfile(null);
    setProfileModalUserId(null);
  }, []);

  const cardBase =
    "bg-white/95 backdrop-blur rounded-2xl overflow-hidden cursor-pointer transition-all shadow-[0_8px_30px_rgba(15,23,42,0.12)] relative hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,23,42,0.18)]";
  const cardSpecial =
    "border-[3px] border-[#ff6b9d] shadow-[0_4px_16px_rgba(255,107,157,0.3)] hover:shadow-[0_8px_24px_rgba(255,107,157,0.4)]";
  const cardUnread = "shadow-[0_4px_12px_rgba(100,180,255,0.2)]";

  const renderedMatches = useMemo(() => {
    return matches.map((match) => {
      const unread = unreadByMatch[match.matchId] || {};
      return {
        ...match,
        unreadSpecialCount:
          unread.unreadSpecialCount ?? match.unreadSpecialCount ?? 0,
        unreadNormalCount:
          unread.unreadNormalCount ?? match.unreadNormalCount ?? 0,
        hasUnreadSpecial:
          unread.hasUnreadSpecial ?? match.hasUnreadSpecial ?? false,
      };
    });
  }, [matches, unreadByMatch]);

  return (
    <AuthBackground>
      <Navbar />

      <div className="flex-1 flex flex-col max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-8 w-full">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">
          Your Matches
        </h1>

        <LoadingAnimation isVisible={loading} />
        <div className="flex-1">
          {!loading && <div></div>}

          {!loading && matches.length === 0 && (
            <div className="text-center text-gray-600 text-base sm:text-lg mt-10">
              <p>No matches yet. Keep swiping!</p>
            </div>
          )}

          {!loading && renderedMatches.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {renderedMatches.map((match) => {
                const unreadSpecialCount = match.unreadSpecialCount;
                const unreadNormalCount = match.unreadNormalCount;
                const hasUnreadSpecial = match.hasUnreadSpecial;

                return (
                  <div
                    key={match.matchId}
                    className={`${cardBase} ${
                      hasUnreadSpecial ? cardSpecial : ""
                    } ${
                      unreadSpecialCount > 0 || unreadNormalCount > 0
                        ? cardUnread
                        : ""
                    }`}
                  >
                    {hasUnreadSpecial && (
                      <div className="absolute top-2 sm:top-2.5 right-2 sm:right-2.5 bg-gradient-to-br from-[#ff6b9d] to-[#ff8fab] text-white px-2 sm:px-3 py-1 rounded-full text-xs font-bold z-10">
                        💖 Special
                      </div>
                    )}

                    {/* Photo - Click to open chat */}
                    <div
                      className="w-full h-[260px] sm:h-[300px] overflow-hidden cursor-pointer"
                      onClick={() => handleChatClick(match.matchId)}
                    >
                      <img
                        src={match.profilePhotoUrl}
                        alt={match.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Profile info - Click to open profile preview */}
                    <div
                      className="p-4 sm:p-5 cursor-pointer"
                      onClick={(e) => handleProfileClick(match, e)}
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-1">
                        {match.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500 mb-2">
                        {match.age} years old
                      </p>
                      {match.bio && (
                        <p className="text-xs sm:text-sm text-gray-500 mb-3 line-clamp-2">
                          {match.bio}
                        </p>
                      )}

                      {(unreadSpecialCount > 0 || unreadNormalCount > 0) && (
                        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
                          {unreadSpecialCount > 0 && (
                            <span className="inline-block bg-gradient-to-br from-[#ff6b9d] to-[#ff8fab] text-white px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-semibold">
                              {unreadSpecialCount} special
                            </span>
                          )}
                          {unreadNormalCount > 0 && (
                            <span className="inline-block bg-[#64b4ff] text-white px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-semibold">
                              {unreadNormalCount} unread
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />

      <UserProfileModal
        userId={profileModalUserId}
        isOpen={profileModalOpen}
        onClose={handleClosePreview}
        prefetchedProfile={selectedProfile}
      />
    </AuthBackground>
  );
}

export default Matches;
